import React from 'react';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import * as RunContextMenu from './RunContextMenu';
import {SidepanelRun} from './RunsSidepanelConstants';

export interface RunListingExtras {
  runSchemas?: Array<AssayRunSchema> | null;
}

export type RunColumnExtras = Pick<RunListingExtras, 'runSchemas'>;

export default class RunListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.RUN> {
  listingRendererType = ListingRendererType.RUN;

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon={ModelTypeConstants.ASSAY_RUN} className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    items: Array<SidepanelRun>,
    {runSchemas}: RunListingExtras
  ): Array<ColumnConfiguration<SidepanelRun>> {
    const runSchemasById = runSchemas ? _.indexBy(runSchemas, 'id') : {};
    let columns: Array<ColumnConfiguration<SidepanelRun>> = _.compact([
      {
        column: {
          accessorKey: ColumnAccessorKey.NAME,
          header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
          flexGrow: 1,
          isSortable: false,
          sortKey: SortKey.SORT_NAME_KEY,
          width: 200,
        },
        render: (item) => (
          <span key="name" className="sidepanel-primaryLabel">
            {item.name}
          </span>
        ),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.SCHEMA,
          header: getColumnHeaderFromSortKey(SortKey.SORT_SCHEMA_NAME),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_SCHEMA_NAME,
        },
        render: (item) => runSchemasById[item.schemaId]?.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATOR,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATOR_NAME),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATOR_NAME,
        },
        render: (item) => getPartyDisplayString(item.creator),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
    ]);

    // The methods in ExpandedTableTagUtil expect all items to have a `schema` field, so we need
    // to add one to each run
    const runsWithSchema: Array<SidepanelRun & {schema: AssayRunSchema}> = items
      .map((run) => ({
        ...run,
        schema: runSchemasById[run.schemaId],
      }))
      .filter(
        (runWithOptionalSchema: SidepanelRun & {schema: AssayRunSchema | undefined}) =>
          runWithOptionalSchema.schema != null
      ) as Array<SidepanelRun & {schema: AssayRunSchema}>;
    const tagColumns = ExpandedTableTagUtil.listSortedTagColumns(runsWithSchema);
    if (tagColumns.length) {
      columns = columns.concat(
        tagColumns.map((column: ScrollableTableColumn) => ({
          column,
          render: (run: SidepanelRun) => {
            const tagsByColumn = ExpandedTableTagUtil.mapTagsToColumns({
              tags: run.tags,
              tagColumns: [column],
            });
            return tagsByColumn.tagFields[column.accessorKey];
          },
        }))
      );
    }

    return columns;
  }

  getNonPluginRowContentHeight(item: SidepanelRun): number {
    return ExpandedTableTagUtil.getRowHeight(item.tags);
  }

  openContextMenu(item: SidepanelRun, e: BasicEvent, extras: unknown): void {
    RunContextMenu.open(e.nativeEvent, item);
  }
}
